import { BillingFormFields } from "../../NormasTeachingCheckout/BillingFormSection.tsx";
import {
  CreateExternalSettlementPayload,
  ExternalOrderPaymentType,
  ExternalSettlementGateway,
  GATEWAYS_SELLING_CATALOG_PRODUCTS,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import {
  toBonificoBusinessInformation,
  toBonificoCustomerTaxCode,
} from "../../ProductService/BonificoOrder/toBonificoBusinessInformation.ts";

export type ExternalSettlementFormValues = BillingFormFields & {
  gateway: ExternalSettlementGateway;
  otherGatewayName?: string;
  externalOrderId: string;
  externalProductId?: string;
  normasteachingProductId: string;
  customer: { email: string; firstName: string; lastName: string };
  receiptDate: string;
  originalPaymentType: ExternalOrderPaymentType;
  numberOfInstallments?: number;
  totalAmountInEuros: number;
  amountAlreadyPaidInEuros: number;
  settlementAmountInEuros: number;
};

export function sellsCatalogProducts(
  gateway: ExternalSettlementGateway,
): boolean {
  return GATEWAYS_SELLING_CATALOG_PRODUCTS.includes(gateway);
}

export function originalPaymentTypeOf(
  gateway: ExternalSettlementGateway,
  chosenPaymentType: ExternalOrderPaymentType,
): ExternalOrderPaymentType {
  return sellsCatalogProducts(gateway)
    ? ExternalOrderPaymentType.OneTime
    : chosenPaymentType;
}

export function eurosToCents(euros: number): number {
  return Math.round(euros * 100);
}

export function remainingAmountInEuros(
  totalAmountInEuros: unknown,
  amountAlreadyPaidInEuros: unknown,
): number | undefined {
  const total = Number(totalAmountInEuros);
  const alreadyPaid = Number(amountAlreadyPaidInEuros);
  if (
    totalAmountInEuros === "" ||
    amountAlreadyPaidInEuros === "" ||
    !Number.isFinite(total) ||
    !Number.isFinite(alreadyPaid)
  )
    return undefined;
  const remainingInCents = eurosToCents(total) - eurosToCents(alreadyPaid);
  return remainingInCents > 0 ? remainingInCents / 100 : undefined;
}

export function toExternalSettlementPayload(
  values: ExternalSettlementFormValues,
): CreateExternalSettlementPayload {
  const isOtherGateway = values.gateway === ExternalSettlementGateway.Other;
  const externalProductId = sellsCatalogProducts(values.gateway)
    ? undefined
    : values.externalProductId?.trim();
  const originalPaymentType = originalPaymentTypeOf(
    values.gateway,
    values.originalPaymentType,
  );
  const numberOfInstallments =
    originalPaymentType === ExternalOrderPaymentType.Installments
      ? values.numberOfInstallments
      : undefined;
  return {
    gateway: values.gateway,
    otherGatewayName: isOtherGateway
      ? values.otherGatewayName?.trim()
      : undefined,
    externalOrderId: values.externalOrderId.trim(),
    externalProductId: externalProductId || undefined,
    normasteachingProductId: values.normasteachingProductId,
    customer: {
      email: values.customer.email.trim(),
      firstName: values.customer.firstName.trim(),
      lastName: values.customer.lastName.trim(),
      taxCode: toBonificoCustomerTaxCode(values),
    },
    businessInformation: toBonificoBusinessInformation(values),
    receiptDate: values.receiptDate,
    originalPaymentType,
    numberOfInstallments: Number.isFinite(numberOfInstallments)
      ? numberOfInstallments
      : undefined,
    totalAmountInCents: eurosToCents(values.totalAmountInEuros),
    amountAlreadyPaidInCents: eurosToCents(values.amountAlreadyPaidInEuros),
    settlementAmountInCents: eurosToCents(values.settlementAmountInEuros),
  };
}
